"use client";

import { useEffect, useState } from "react";
import Preloader from "./preloader";
import { SidebarInset, SidebarProvider } from "./ui/sidebar";
import { AppSidebar } from "./app-sidebar";
import { useUserStore } from "@/stores/user";
import { useStoreHydration } from "@/hooks/useStoreHydration";
import { useOrganizationStore } from "@/stores/organization";
import OrganizationCreateDialog from "./organization-create-dialog";
import { login, logout } from "@/app/actions";
import { deleteCookie, getCookie } from "@/lib/cookies";
import InviteAcceptDialog from "./invite-accept-dialog";
import { updateOrganizationMemberByInivitationDetails } from "@/services/member.client";

type AppBootstrapProps = {
  children: React.ReactNode;
};

export default function AppBootstrap({ children }: AppBootstrapProps) {
  const isHydrated = useStoreHydration();
  const { fetchUser, user } = useUserStore();
  const { fetchOrganization } = useOrganizationStore();
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const [isCreateOrganization, setIsCreateOrganization] = useState(false);
  const [invitation, setInvitation] = useState<{
    orgId: string;
    userId: string;
    token: string;
  } | null>(null);

  useEffect(() => {
    (async () => {
      if (!isHydrated) return;

      const inviteParams = getCookie("inviteParams");

      if (inviteParams) {
        const params = new URLSearchParams(inviteParams);
        const orgId = params.get("orgId");
        const userId = params.get("userId");
        const token = params.get("token");

        if (orgId && userId && token) {
          setInvitation({ orgId, userId, token });
        }
      }

      try {
        await fetchUser(true);
      } catch (error: any) {
        if (!inviteParams) {
          console.warn("Failed to fetch user", error.message);
          // On error, forcibly redirect to login page if there's no logged in user
          setIsBootstrapped(false);
          logout();
        } else {
          setIsBootstrapped(true);
        }
      }
    })();
  }, [fetchUser, fetchOrganization, isHydrated]);

  useEffect(() => {
    (async () => {
      if (!user) return;

      if (user && user.organizations.length === 0) {
        setIsCreateOrganization(true);
        setIsBootstrapped(true);
        return;
      }

      const lastSelectedOrgId = getCookie("lastSelectedOrganization");
      const orgId = lastSelectedOrgId || user.organizations[0];

      if (orgId && user.organizations.length > 0) {
        try {
          // Fetch organization from either lastSelectedOrgId or first organization
          await fetchOrganization(orgId, true);
          setIsBootstrapped(true);
          return;
        } catch (error: any) {
          try {
            console.warn(
              `There was an issue fetching the last selected organization '${orgId}', attempting first. ${error.message}`,
            );
            await fetchOrganization(user.organizations[0], true);
            setIsBootstrapped(true);
            return;
          } catch (error: any) {
            console.warn(
              `Unable to fetch organization '${user.organizations[0]}'. ${error.message}`,
            );
            // logout();
          }
        }
      }
      setIsCreateOrganization(true);
      setIsBootstrapped(true);
    })();
  }, [fetchOrganization, user]);

  if (!isBootstrapped) return <Preloader />;

  return (
    <>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <main className="flex w-full min-w-0 shrink flex-col">
            <div className="lb-scrollbar max-h-[calc(100svh-theme(spacing.4))] w-full min-w-0 flex-1">
              {children}
            </div>
          </main>
        </SidebarInset>
      </SidebarProvider>
      {/* Prompts when user has no organizations */}
      <OrganizationCreateDialog
        controlledOpen={isCreateOrganization && !invitation && !!user}
        isForced={true}
        onSuccess={() => {
          setIsCreateOrganization(false);
        }}
      />
      {/* Prompts when an invitation has been opened */}
      <InviteAcceptDialog
        controlledOpen={!!invitation}
        user={user}
        invitation={invitation!}
        onAccept={async (updateData) => {
          deleteCookie("inviteParams", "/");

          try {
            const { userId, organizationId } =
              await updateOrganizationMemberByInivitationDetails(updateData);

            if (user && user.id === userId) {
              await fetchOrganization(organizationId, true);
              setInvitation(null);
            } else {
              // @TODO: It would be best if we could just log them in here
              setIsBootstrapped(false);
              setInvitation(null);
              logout();
            }
          } catch (error: any) {
            console.warn("Failed to accept invitation", error.message);
            // On error, forcibly redirect to login page if there's no logged in user
            if (user === null) {
              setIsBootstrapped(false);
              logout();
            }
          }
        }}
        onDecline={async (updateData) => {
          deleteCookie("inviteParams", "/");

          // Update if declined, ignore if dismissed
          if (updateData) {
            await updateOrganizationMemberByInivitationDetails(updateData);
          }

          if (user === null) {
            setIsBootstrapped(false);
            await login();
          } else {
            setInvitation(null);
          }
        }}
      />
    </>
  );
}
